/**
 * Release-note ("new version available") helpers.
 *
 * A support user publishes a release note; bookings-infra fans it out as a
 * `release_note.published` notification per Active staff member (persisted +
 * pushed over Pusher). The portal toasts it ONLY when the open tab is out of
 * date — i.e. the note was created after this page loaded — and only once per
 * tab session. See ReleaseNoteWatcher / useReleaseNoteWatcher.
 */
import type { Notification } from "../types/notifications";

export const RELEASE_NOTE_EVENT = "release_note.published";

/** sessionStorage key holding release-note ids already toasted in this tab. */
export const TOASTED_STORAGE_KEY = "pagoda:release-notes-toasted";

/** Keep the toasted-id list bounded; oldest ids fall off first. */
export const MAX_TOASTED_IDS = 50;

/**
 * When this page (JS bundle) was loaded. Computed once at module load: a note
 * created after this instant means the running code may be out of date.
 */
export const PAGE_LOADED_AT: number =
  (typeof performance !== "undefined" && performance.timeOrigin) || Date.now();

/**
 * Parse a notification timestamp to epoch ms (NaN when unparseable).
 *
 * The notifications API serializes `created_dt` (a TIMESTAMPTZ) as a naive
 * "YYYY-MM-DD HH:MM:SS" in UTC; the Alerts list already treats it as UTC via
 * `moment.utc`. Live Pusher items get a full ISO string from addNotification.
 * Naive values are therefore read as UTC rather than (wrongly) local time.
 */
export function parseNotificationTimestamp(
  value: string | null | undefined,
): number {
  if (!value || typeof value !== "string") return NaN;
  const trimmed = value.trim();
  const isoLike = trimmed.replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(isoLike);
  const isDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoLike);
  return Date.parse(isDateTime && !hasZone ? `${isoLike}Z` : isoLike);
}

/**
 * Whether a note created at `createdMs` is newer than the page.
 *
 * Server timestamps are truncated to whole seconds, so compare against the
 * page-load second: a note published in the same second the page loaded is
 * treated as newer (a harmless extra toast) rather than silently missed.
 */
export function isCreatedAfterPageLoad(
  createdMs: number,
  pageLoadedAt: number = PAGE_LOADED_AT,
): boolean {
  if (!Number.isFinite(createdMs)) return false;
  return createdMs >= Math.floor(pageLoadedAt / 1000) * 1000;
}

/** The id a release-note toast is keyed on (stable across locations). */
export function releaseNoteToastKey(
  notification: Pick<Notification, "notification_id" | "body">,
): string {
  return notification.body?.release_note_id || notification.notification_id;
}

/**
 * True when this notification should raise the "Refresh" toast: it is an
 * unread release note created after the page loaded that this tab has not
 * toasted yet.
 */
export function isToastableReleaseNote(
  notification: Notification | null | undefined,
  toastedIds: ReadonlySet<string> | readonly string[],
  pageLoadedAt: number = PAGE_LOADED_AT,
): boolean {
  if (!notification) return false;
  if (notification.event_name !== RELEASE_NOTE_EVENT) return false;
  if (notification.is_read) return false;
  const createdMs = parseNotificationTimestamp(notification.created_dt);
  if (!isCreatedAfterPageLoad(createdMs, pageLoadedAt)) return false;
  const key = releaseNoteToastKey(notification);
  const toasted =
    toastedIds instanceof Set
      ? toastedIds.has(key)
      : (toastedIds as readonly string[]).includes(key);
  return !toasted;
}

/** Ids already toasted in this tab. Never throws (private mode, blocked storage). */
export function getToastedIds(): string[] {
  try {
    const raw = window.sessionStorage.getItem(TOASTED_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

/** Remember that `id` was toasted. Never throws; caps the list at 50 ids. */
export function markToasted(id: string): void {
  try {
    const ids = getToastedIds().filter((existing) => existing !== id);
    ids.push(id);
    window.sessionStorage.setItem(
      TOASTED_STORAGE_KEY,
      JSON.stringify(ids.slice(-MAX_TOASTED_IDS)),
    );
  } catch {
    // Storage unavailable — the in-memory set in the watcher still dedupes.
  }
}

/** Only absolute https URLs may be rendered as links (no javascript:, http:, etc.). */
export function isSafeHttpsUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    return new URL(value.trim()).protocol === "https:";
  } catch {
    return false;
  }
}
